<template>
  <section class="eva-page">
    <v-row>
      <v-col cols="12" offset="0" md="10" offset-md="1">

        <!-- Topo -->
        <div v-if="!has_component" class="eva-top">
          <v-breadcrumbs
            large
            :items="crumbs"
            divider="/"
            class="pl-0 eva-breadcrumbs"
          />

          <div class="eva-titlebar">
            <div class="eva-title">
              <v-icon color="primary" class="mr-2">mdi-van-passenger</v-icon>
              <span class="primary--text">Gestão de Avaliações</span>
            </div>

            <v-spacer />

            <!-- Exportar avaliações -->
            <v-btn
              v-if="false"
              color="primary"
              depressed
              class="eva-btn mr-2"
              @click="openExportDialog"
            >
              <v-icon left>mdi-file-export</v-icon>
              Exportar avaliações
            </v-btn>

            <!-- Nova avaliação -->
            <v-btn
              v-if="$store.getters.hasPermission(['super', 'supplier_evaluations.write'])"
              color="primary"
              depressed
              class="eva-btn"
              @click="$router.push('/evaluations/create')"
            >
              <v-icon left>mdi-plus</v-icon>
              Nova avaliação
            </v-btn>
          </div>
        </div>

        <!-- Card principal -->
        <v-card class="eva-card mt-6" elevation="10">
          <v-card-title class="eva-card__title primary white--text">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="white">mdi-clipboard-text</v-icon>
              <span class="font-weight-bold">
                {{ has_component ? 'Avaliações do fornecedor' : 'Lista de avaliações' }}
              </span>
            </div>

            <v-spacer />

            <v-chip color="white" outlined class="eva-chip">
              <v-icon left small>mdi-format-list-bulleted</v-icon>
              {{ total }} registos
            </v-chip>
          </v-card-title>

          <v-card-text class="pt-6">
            <div style="position:relative">
              <v-data-table
                :headers="headers"
                :items="items"
                :server-items-length="total"
                :loading="loading"
                :footer-props="tableFooter"
                :options.sync="options"
                class="eva-table"
                locale="pt-pt"
              >
                <template v-slot:item.supplier_id="{ item }">
                  <span class="eva-mono">{{ item.supplier.Fornecedor }} - {{item.supplier.Nome}}</span>
                </template>
                <template v-slot:item.central_cost="{ item }">
                  <span class="eva-mono">{{ item.central_cost.Codigo }} - {{item.central_cost.Descricao}}</span>
                </template>

                <template v-slot:item.evaluation_ci="{ item }">
                  <v-chip
                    small
                    dark
                    :style="{ backgroundColor: evaluationColor(item.evaluation_ci) }"
                  >
                    {{ formatEvaluation(item.evaluation_ci) }}
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <IconView class="mr-2" @click="viewEvaluation(item)" />
                </template>

                <template v-slot:no-data>
                  <div class="pa-6 grey--text text--darken-1">
                    Sem resultados.
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>

        <!-- Modal Exportação -->
        <v-dialog v-model="exportDialog" max-width="520" persistent>
          <v-card class="eva-modal" elevation="12">
            <v-card-title class="primary white--text">
              <v-icon class="mr-2" color="white">mdi-export</v-icon>
              Exporrtação
              <v-spacer />
              <v-btn icon color="white" @click="exportDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text class="pt-6">
              <v-autocomplete
                label="Ano *"
                :items="exportYears"
                dense
                outlined
                class="eva-input"
                v-model="exportYear"
                prepend-inner-icon="mdi-calendar"
              />
              <div class="eva-hint">
                Seleciona o ano que queres exportar.
              </div>
            </v-card-text>

            <v-card-actions class="px-6 pb-6">
              <v-spacer />
              <v-btn text @click="exportDialog = false">
                Cancelar
              </v-btn>
              <v-btn
                class="success white--text"
                :disabled="!exportYear"
                depressed
                @click="exportEvaluations"
              >
                <v-icon left>mdi-download</v-icon>
                Exportar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

      </v-col>
    </v-row>
  </section>
</template>

<script>
import IconView from '@/components/UI/IconView.vue';
import SupplierEvaluation from '@/api/SupplierEvaluation.js'
import dayjs from 'dayjs';

const RED = { r: 211, g: 47, b: 47 }     // vermelho forte
const YELLOW = { r: 251, g: 192, b: 45 } // amarelo
const GREEN = { r: 56, g: 142, b: 60 }   // verde forte

export default {
  components: { IconView },
  props: {
    has_component: {
      typeof: Boolean,
      default: false
    }
  },
  watch: {
    options: {
      handler() {
        setTimeout(() => { this.searchSupplierEvaluation() }, 10);
      },
      deep: true,
    },
  },
  beforeCreate() {
    document.title = "ODC - Gestão de Avaliações";
  },
  data: () => ({
    headers: [
      { text: 'Fornecedor', sortable: false, value: 'supplier_id' },
      { text: 'Centro Custo', sortable: false, value: 'central_cost' },
      { text: 'Ano', sortable: false, value: 'year' },
      { text: 'Avaliação', sortable: false, value: 'evaluation_ci' },
      { text: 'Responsável', sortable: false, value: 'user_responsible.name' },
      { text: 'Opções', value: 'actions', sortable: false },
    ],
    items: [],
    total: 0,
    loading: false,
    searching: false,
    tableFooter: {
      'items-per-page-options': [5, 10, 15, 20],
      'items-per-page-text': "Linhas por página"
    },
    options: {},
    filter: { supplier_id: null },

    // Exportação
    exportDialog: false,
    exportYear: null,
  }),
  computed: {
    crumbs() {
      return [
        { text: 'Dashboard', disabled: false, to: '/admin', exact: true },
        { text: 'Gestão de Avaliações', disabled: true, to: window.location.href, exact: true },
      ];
    },
    exportYears() {
      const start = 2020;
      const current = new Date().getFullYear();
      const years = [];
      for (let y = current; y >= start; y--) years.push(y);
      return years;
    }
  },
  mounted() {
    if (!this.$store.getters.hasPermission(["super", "supplier_evaluations.read"])) {
      this.$router.push('/forbidden');
    }

    if (this.has_component) this.filter.supplier_id = this.$route.params.id;

    this.fillBaseData();
  },
  methods: {
    formatEvaluation(val) {
      if (val === null || val === undefined || val === '') return '-'
      const n = Number(val)
      if (Number.isNaN(n)) return '-'
      return n.toFixed(2)
    },
    interpolateColor(c1, c2, factor) {
      return {
        r: Math.round(c1.r + (c2.r - c1.r) * factor),
        g: Math.round(c1.g + (c2.g - c1.g) * factor),
        b: Math.round(c1.b + (c2.b - c1.b) * factor),
      }
    },
    rgbToCss({ r, g, b }) {
      return `rgb(${r}, ${g}, ${b})`
    },
    evaluationColor(value) {
      if (value == null) return '#9E9E9E' // cinzento

      const v = Math.max(0, Math.min(20, Number(value)))

      if (v <= 10) {
        const factor = v / 10
        return this.rgbToCss(this.interpolateColor(RED, YELLOW, factor))
      }

      const factor = (v - 10) / 10
      return this.rgbToCss(this.interpolateColor(YELLOW, GREEN, factor))
    },
    getTableDate(date) {
      return date != null ? dayjs(date).toDate().toLocaleDateString() : "-";
    },
    fillBaseData() {
      if (this.has_component == false) {
        const key = "suppliers-evaluation-" + this.$store.state.user.id;
        if (localStorage[key] != undefined) {
          let data = JSON.parse(localStorage[key]);
          this.filter = data.filter;
          this.options = data.options;
        }
      }
    },
    searchSupplierEvaluation(event) {
      if (!!event && event.type == 'submit') this.options.page = 1;

      this.loading = true;

      let filter = { ...this.filter };

      let request = {
        sortBy: this.options.sortBy,
        sortDesc: this.options.sortDesc,
        page: this.options.page,
        limit: this.options.itemsPerPage,
        supplier_id: this.$route.params.id
      };

      Object.assign(request, filter);

      if (this.has_component == false) {
        localStorage["suppliers-evaluation-" + this.$store.state.user.id] = JSON.stringify({
          filter: this.filter,
          options: this.options,
        });
      }

      SupplierEvaluation.search(request).then(response => {
        let data = response.data;
        this.total = data.total;
        this.items = data.data.map((item) => {
          this.searching = false;
          return item;
        });
        this.loading = false;
      });
    },

    viewEvaluation(item) {
      this.$router.push('/evaluations/' + item.id);
    },

    openExportDialog() {
      this.exportYear = null;
      this.exportDialog = true;
    },

    exportEvaluations() {
      let data = {
        year: this.exportYear
      }
      SupplierEvaluation.exportEvaluations(data);
    },
  }
};
</script>

<style scoped>
.eva-page {
  padding-bottom: 32px;
}

.eva-top {
  margin-top: 8px;
}

.eva-breadcrumbs :deep(.v-breadcrumbs__item) {
  font-size: 13px;
}

.eva-titlebar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}

.eva-title {
  display: flex;
  align-items: center;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.2px;
}

.eva-btn {
  border-radius: 12px;
}

.eva-card {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.06);
}

.eva-card__title {
  font-weight: 800;
  letter-spacing: 0.2px;
}

.eva-chip {
  border-radius: 999px;
}

.eva-table {
  border-radius: 14px;
  overflow: hidden;
}

.eva-table :deep(thead th) {
  font-weight: 800;
  color: rgba(0,0,0,0.75);
  letter-spacing: 0.2px;
}

.eva-table :deep(tbody tr:hover) {
  background: rgba(0,0,0,0.02);
}

.eva-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-weight: 700;
  color: rgba(0,0,0,0.75);
}

.eva-modal {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.06);
}

.eva-input :deep(.v-input__slot) {
  border-radius: 12px !important;
  background: rgba(0,0,0,0.015);
}

.eva-hint {
  margin-top: 8px;
  font-size: 13px;
  color: rgba(0,0,0,0.6);
}
</style>
